import { requestAudioPlayback } from "../actions/mediaActions";
import type { PrototypeState } from "../controllerState";
import { removeClass } from "../domUtils";
import { getTopic } from "../topicStore";
import {
  mountedFor,
  pauseMediaPlayer,
  playMediaPlayer,
  stopVideoPlayerEnhancements,
  unmountMediaPlayer,
} from "../mediaPlayer";
import { MEDIA_COMPLETED_RATIO } from "../mediaPlayerProgress";
import { showArticle } from "../navigationController";
import { showPlayer } from "../playerUiController";
import type { Root } from "../prototypeTypes";
import { recordRuntimeError } from "../prototypeRuntimeSupport";
import { setRuntime } from "../runtimeAccessor";
import { beginAudioHandoffCommandIntent, isAudioHandoffIntentCurrent } from '../audioHandoffController';

export function videoShareUrl(state: PrototypeState): string {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  const topic = state.runtime.activeCourseId || "topic";
  const chapter = state.runtime.activeChapterIndex || 1;
  return `${base}/topics/${encodeURIComponent(topic)}/chapters/${chapter}`;
}

export async function transferVideoProgressToAudio(
  root: Root,
  state: PrototypeState,
  options: { play: boolean },
): Promise<void> {
	const intent = beginAudioHandoffCommandIntent(root);
  await exitDocumentFullscreen(root);
	if (!isAudioHandoffIntentCurrent(root, intent)) return;
  const video = mountedFor(root, "video");
  const topicId = video?.topicId ?? state.runtime.activeCourseId;
  const chapterIndex = video?.chapterIndex ?? state.runtime.activeChapterIndex;
  if (topicId) state.runtime.activeCourseId = topicId;
  if (Number.isFinite(chapterIndex) && chapterIndex > 0) state.runtime.activeChapterIndex = chapterIndex;

  const duration = video ? playbackDuration(video.element, video.fallbackDurationSeconds) : 0;
  const current = video ? playbackCurrent(video.element) : 0;
  if (topicId && Number.isFinite(chapterIndex) && chapterIndex > 0 && duration > 0 && current > 0) {
    const key = `${topicId}:${chapterIndex}`;
    state.runtime.resumeProgressByChapter = {
      ...(state.runtime.resumeProgressByChapter ?? {}),
		[key]: Math.min(100, Math.max(0, Math.round((current / duration) * 100))),
    };
		state.runtime.mediaResumeSecondsByChapter = {
			...(state.runtime.mediaResumeSecondsByChapter ?? {}),
			[key]: current,
		};
  }

  removeClass(root, "videoFullscreen", "active");
  unmountMediaPlayer(root, "video", { force: true });
  stopVideoPlayerEnhancements(root);
  if (Number.isFinite(chapterIndex) && chapterIndex > 0) showArticle(chapterIndex, root, state.effectiveAccessPlan);
  // Mount with the target play state directly: requestAudioPlayback must not
  // autoplay when options.play is false, otherwise the audio briefly starts
  // and then pauses again (the play→pause→play flicker on the Back button).
	await requestAudioPlayback(state.runtime, state.effectiveAccessPlan, root, recordRuntimeError, { play: options.play }, intent);
  // Final-state guarantee: if the mount skipped autoplay, make sure the
  // element matches the requested state (play() on an already-playing
  // element is a no-op).
  if (options.play) await playMediaPlayer(root, "audio");
  else pauseMediaPlayer(root, "audio");
  showPlayer(root);
}

/**
 * Corner Close semantics: end the video session outright -- stop playback,
 * return to the article, mount no audio. Continuing in audio is Back's and
 * the Listen button's job, never a side effect of closing. The unmount path
 * persists resume progress, so a later Watch/Listen resumes where it stopped.
 */
export async function closeVideoPlaybackSession(root: Root, state: PrototypeState): Promise<void> {
  const video = mountedFor(root, "video");
  const topicId = video?.topicId ?? state.runtime.activeCourseId;
  const chapterIndex = video?.chapterIndex ?? state.runtime.activeChapterIndex;
  if (topicId) state.runtime.activeCourseId = topicId;
  if (Number.isFinite(chapterIndex) && chapterIndex > 0) state.runtime.activeChapterIndex = chapterIndex;
  await exitDocumentFullscreen(root);
  removeClass(root, "videoFullscreen", "active");
  unmountMediaPlayer(root, "video", { force: true });
  stopVideoPlayerEnhancements(root);
  if (Number.isFinite(chapterIndex) && chapterIndex > 0) showArticle(chapterIndex, root, state.effectiveAccessPlan);
}

export async function exitDocumentFullscreen(root: Root): Promise<void> {
  const doc = root instanceof Document ? root : root.ownerDocument ?? document;
  const webkitDocument = doc as Document & { webkitFullscreenElement?: Element | null; webkitExitFullscreen?: () => Promise<void> };
  if (!doc.fullscreenElement && !webkitDocument.webkitFullscreenElement) return;
  try {
    if (doc.fullscreenElement) await doc.exitFullscreen?.();
    else await webkitDocument.webkitExitFullscreen?.();
  } catch {
    // A fullscreen exit can be denied during rapid PiP/browser transitions.
    // The visual teardown still proceeds so the UI does not stay trapped.
  }
}

export async function resolveTopicResumeTarget(
  root: Root,
  state: PrototypeState,
): Promise<{ topicId: string; chapterIndex: number; progressPercent?: number; mediaResumePositionSeconds?: number | null; chapterId?: string } | null> {
  const topicId = state.runtime.activeCourseId;
  if (!topicId) return null;
  try {
    const resp = await state.runtime.api.getResumeCard(50);
    const candidates = (resp.recent ?? [])
      .filter((item) => item.topicSlug === topicId || item.topicId === topicId)
      .sort((a, b) => (b.chapterIndex - a.chapterIndex) || Date.parse(b.updatedAt ?? "") - Date.parse(a.updatedAt ?? ""));
    const best = candidates[0];
    if (best) {
      return {
        topicId: best.topicSlug || best.topicId || topicId,
        chapterIndex: best.chapterIndex,
        progressPercent: best.progressPercent,
			mediaResumePositionSeconds: best.mediaResumePositionSeconds,
        chapterId: best.chapterId,
      };
    }
  } catch (error) {
    recordRuntimeError(state.runtime, error);
  }
  const fromRuntime = Object.entries(state.runtime.resumeProgressByChapter ?? {})
    .filter(([key]) => key.startsWith(`${topicId}:`))
    .map(([key, pct]) => ({ chapterIndex: Number(key.split(":")[1]), progressPercent: pct }))
    .filter((item) => Number.isInteger(item.chapterIndex) && item.chapterIndex > 0)
    .sort((a, b) => (b.chapterIndex - a.chapterIndex) || ((b.progressPercent ?? 0) - (a.progressPercent ?? 0)))[0];
  return { topicId, chapterIndex: fromRuntime?.chapterIndex ?? state.runtime.activeChapterIndex ?? 1, progressPercent: fromRuntime?.progressPercent };
}

export function setActiveTarget(
  root: Root,
  state: PrototypeState,
  topicId: string,
  chapterIndex: number,
  progressPercent?: number,
): void {
  state.runtime.activeCourseId = topicId;
  state.runtime.activeChapterIndex = chapterIndex;
  if (typeof progressPercent === "number" && progressPercent > 0 && progressPercent < 98) {
    state.runtime.resumeProgressByChapter = {
      ...(state.runtime.resumeProgressByChapter ?? {}),
      [`${topicId}:${chapterIndex}`]: progressPercent,
    };
  }
  setRuntime(root, state.runtime);
}

/**
 * Whether the target chapter's LOCAL cursor marks it finished. Same-session
 * completions are present here (playback syncs write up to 100%); a fresh page
 * load is not — those entries degrade to the restore-layer restart-from-0:00.
 * Cheap and synchronous so callers can keep first paint in the click frame
 * unless a redirect is actually due.
 */
export function isFinishedAudioTarget(
	state: PrototypeState,
	target: { topicId: string; chapterIndex: number },
): boolean {
	const percent = state.runtime.resumeProgressByChapter?.[`${target.topicId}:${target.chapterIndex}`] ?? 0;
	return Boolean(target.topicId) && percent >= MEDIA_COMPLETED_RATIO * 100;
}

/**
 * Non-switch playback entries (article Listen pill) on a finished chapter jump
 * to the NEXT chapter at 0:00 — the same rule as the Home Recent play button
 * (final chapter wraps to Chapter 1). Returns the original target untouched
 * when the chapter is not finished or the chapter list is unavailable.
 */
export async function redirectCompletedAudioTarget(
	root: Root,
	state: PrototypeState,
	target: { topicId: string; chapterIndex: number },
): Promise<{ topicId: string; chapterIndex: number }> {
	if (!isFinishedAudioTarget(state, target)) return target;
	try {
    const response = await state.runtime.api.listTopicChapters(target.topicId);
    const chapters = (Array.isArray(response.chapters) ? response.chapters : [])
      .slice()
      .sort((a, b) => a.index - b.index);
    if (chapters.length === 0) return target;
    const next = chapters.find((candidate) => candidate.index > target.chapterIndex)
      ?? chapters.find((candidate) => candidate.index === 1)
      ?? chapters[0];
    if (!next || next.index === target.chapterIndex) return target;
    state.runtime.activeChapterIndex = next.index;
    state.runtime.chapterIdByIndex[target.topicId] = {
      ...(state.runtime.chapterIdByIndex[target.topicId] ?? {}),
      [next.index]: next.id,
    };
    // The jump target starts at 0:00: seed both cursor maps with zero so no
    // stale cursor survives for the restore layer to reconcile.
    state.runtime.resumeProgressByChapter = {
      ...(state.runtime.resumeProgressByChapter ?? {}),
      [`${target.topicId}:${next.index}`]: 0,
    };
    state.runtime.mediaResumeSecondsByChapter = {
      ...(state.runtime.mediaResumeSecondsByChapter ?? {}),
      [`${target.topicId}:${next.index}`]: 0,
    };
    (root as unknown as { __activeChapter?: { index: number; title: string; topicTitle: string; topicSlug: string } }).__activeChapter = {
      index: next.index,
      title: next.title || `Chapter ${next.index}`,
      topicTitle: getTopic(target.topicId)?.title || target.topicId,
      topicSlug: target.topicId,
    };
    return { topicId: target.topicId, chapterIndex: next.index };
  } catch {
    // Chapter list unavailable: replay the finished chapter from 0:00.
    return target;
  }
}

export async function ensureActiveAudioTarget(root: Root, state: PrototypeState): Promise<boolean> {
  if (state.runtime.activeCourseId) return true;
  try {
    const response = await state.runtime.api.getResumeCard();
    const resume = response.resume ?? response.recent?.[0] ?? null;
    if (!resume) return false;
    state.runtime.activeCourseId = resume.topicSlug;
    state.runtime.activeChapterIndex = resume.chapterIndex;
    state.runtime.chapterIdByIndex[resume.topicSlug] = {
      ...(state.runtime.chapterIdByIndex[resume.topicSlug] ?? {}),
      [resume.chapterIndex]: resume.chapterId,
    };
	state.runtime.resumeProgressByChapter = {
      ...(state.runtime.resumeProgressByChapter ?? {}),
      [`${resume.topicSlug}:${resume.chapterIndex}`]: resume.progressPercent,
	};
		if (typeof resume.mediaResumePositionSeconds === 'number') {
			state.runtime.mediaResumeSecondsByChapter = {
				...(state.runtime.mediaResumeSecondsByChapter ?? {}),
				[`${resume.topicSlug}:${resume.chapterIndex}`]: resume.mediaResumePositionSeconds,
			};
		}
    showArticle(resume.chapterIndex, root, state.effectiveAccessPlan);
    return true;
  } catch (error) {
    recordRuntimeError(state.runtime, error);
    return false;
  }
}

function playbackDuration(element: HTMLMediaElement, fallback?: number): number {
  return Number.isFinite(element.duration) && element.duration > 0 ? element.duration : fallback ?? 0;
}

function playbackCurrent(element: HTMLMediaElement): number {
  return Number.isFinite(element.currentTime) ? element.currentTime : 0;
}
